import { LoadingOutlined } from '@ant-design/icons';
import { getItemsByInternalIds } from '@knowledge/common/cache/spu';
import { MultiLangName } from '@knowledge/common/hooks/useKnowledgeName';
import { getSpuClassKnowledges, getSpuClassSpus } from '@knowledge/knowledges/singleClassNew/api';
import ConfigKC from '@knowledge/knowledges/singleClassNew/ConfigKC';
import SpuClassProvider, {
  useSpuClassContext,
} from '@knowledge/knowledges/singleClassNew/SpuClass';
import { SpuAreaFormikValue } from '@knowledge/knowledges/singleClassNew/type';
import { getSpuClassList } from '@knowledge/service/api/single';
import { useTranslate } from '@tolgee/react';
import { useRequest } from 'ahooks';
import { Empty, message } from 'antd';
import { Formik } from 'formik';
import { ReactNode, useEffect, useMemo } from 'react';

interface ItemKnowledgeProps {
  spuId: string;
  children: (content: JSX.Element, name?: ReactNode) => JSX.Element;
}

function ItemKnowledge(props: ItemKnowledgeProps) {
  return (
    <SpuClassProvider>
      <ItemKnowledgeImpl {...props} />
    </SpuClassProvider>
  );
}

function ItemKnowledgeImpl({ spuId, children }: ItemKnowledgeProps) {
  const { t } = useTranslate();

  const { loading: spuClassLoading, list, activeSpuClass, setActiveIndex } = useSpuClassContext();

  const { loading: spuClassIdsLoading, data: spuClassIds } = useRequest(
    () => getSpuClassList(spuId).then(({ spuClassInfoList }) => spuClassInfoList.map((_) => _.id)),
    {
      refreshDeps: [spuId],
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  useEffect(() => {
    if (
      list.length &&
      spuClassIds &&
      spuClassIds.length &&
      !spuClassIds.includes(activeSpuClass?.id)
    ) {
      const targetIndex = list.findIndex((_) => _.id === spuClassIds[0]);
      if (targetIndex > -1) {
        setActiveIndex(targetIndex);
      }
    }
  }, [list, spuClassIds, activeSpuClass, setActiveIndex]);

  const { data: knowledges, loading: knowledgeLoading } = useRequest(
    () => getSpuClassKnowledges(activeSpuClass?.id!),
    {
      ready: spuClassIds?.includes(activeSpuClass?.id) || false,
      refreshDeps: [activeSpuClass],
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  const { data, loading } = useRequest(
    () => {
      return getSpuClassSpus(activeSpuClass!.id).then((res) => {
        return getItemsByInternalIds(res, '1');
      });
    },
    {
      ready: spuClassIds?.includes(activeSpuClass?.id) || false,
      refreshDeps: [activeSpuClass],
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  const targetItem = useMemo(() => data?.find((_) => _.externalItemId === spuId), [data, spuId]);

  if (loading || spuClassIdsLoading || spuClassLoading || knowledgeLoading) {
    return children(<LoadingOutlined />);
  }

  if (!data || !knowledges || !targetItem) {
    return children(
      <div className="h-full flex items-center justify-center">
        <Empty description={t('暂无数据')} />
      </div>,
    );
  }

  return children(
    <Formik<SpuAreaFormikValue>
      initialValues={{
        knowledges: knowledges || [],
      }}
      enableReinitialize
      onSubmit={() => {}}
    >
      {({ values }) => {
        return (
          <ConfigKC
            spuClassIds={spuClassIds}
            spuClassSpus={data}
            items={[targetItem]}
            knowledges={values.knowledges}
            loading={knowledgeLoading}
          />
        );
      }}
    </Formik>,
    activeSpuClass && (
      <MultiLangName source={activeSpuClass.nameMultiLang} fallback={activeSpuClass.name} />
    ),
  );
}

export default ItemKnowledge;
